import { type RefObject, useCallback, useRef } from "react";
import { selectOpenedDetailDishTypeId, setOpenedDetailDishTypeId } from "slices/appStateSlice";
import { scrollIntoView } from "utils/scrollIntoView";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

interface UseMealListEventsReturn {
	handleOpen: (dishTypeId: string, ref?: RefObject<HTMLDivElement | null>) => void;
	openDishTypeDetail: (dishTypeId: string) => void;
}

/**
 * Manages UI events for the meal list — opening/closing dish type details and scrolling
 * the selected dish into view. Fridge-lighting concerns live in `useConvergeLighting`.
 *
 * @return {UseMealListEventsReturn} Event handlers for the meal list.
 */
const useMealListEvents = (): UseMealListEventsReturn => {
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const dispatch = useAppDispatch();

	// Latest-ref so handleOpen stays stable across renders while still seeing the current
	// opened detail to implement toggle semantics.
	const openDetailDishTypeIdRef = useRef(openDetailDishTypeId);
	openDetailDishTypeIdRef.current = openDetailDishTypeId;

	const closeOpenedDishTypeDetail = useCallback(() => {
		dispatch(setOpenedDetailDishTypeId(null));
	}, [dispatch]);

	const openDishTypeDetail = useCallback(
		(dishTypeId: string) => {
			dispatch(setOpenedDetailDishTypeId(dishTypeId));
		},
		[dispatch],
	);

	const handleOpen = useCallback(
		(dishTypeId: string, ref?: RefObject<HTMLDivElement | null>) => {
			if (ref) {
				scrollIntoView(ref);
			}
			if (openDetailDishTypeIdRef.current === dishTypeId) {
				closeOpenedDishTypeDetail();
				return;
			}
			openDishTypeDetail(dishTypeId);
		},
		[closeOpenedDishTypeDetail, openDishTypeDetail],
	);

	return {
		handleOpen: handleOpen,
		openDishTypeDetail: openDishTypeDetail,
	};
};

export default useMealListEvents;
